import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    
    // Remove any empty LI elements
    this.#removeEmptyListItems();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Removes empty LI elements from the product grid
   */
  #removeEmptyListItems() {
    const listItems = this.querySelectorAll('li.product-grid__item');
    listItems.forEach((li) => {
      // CRITICAL: Skip skeleton cards - they're placeholders that will be populated
      if (li.hasAttribute('data-skeleton') || li.classList.contains('product-grid__item--skeleton')) {
        return;
      }
      
      // Check if the LI is empty (no text content and no child elements with content)
      const hasContent = li.textContent.trim().length > 0 || 
                         li.querySelector('.sunice-product-card, .product-card, .variant-card-skeleton, img, a[href]');
      if (!hasContent) {
        li.remove();
      }
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
